import { Interface, ZeroAddress, getAddress, keccak256, AbiCoder } from 'ethers'
import {
  concat as viemConcat,
  encodeFunctionData as viemEncodeFunctionData,
  encodePacked as viemEncodePacked,
  type Hex as ViemHex,
} from 'viem'
import type { Hex } from '../types.js'

/**
 * Build the wrapped Module-Safe transaction that proxies a dApp call through
 * the Main Safe via execTransactionFromModule. This is the same shape Zodiac
 * Pilot produces — see VAULT.md "Phase 4 - Configure Zodiac Pilot".
 */

const MAIN_SAFE_IFACE = new Interface([
  'function execTransactionFromModule(address to, uint256 value, bytes data, uint8 operation) returns (bool)',
])

const SAFE_EXEC_IFACE = new Interface([
  'function execTransaction(address to, uint256 value, bytes data, uint8 operation, uint256 safeTxGas, uint256 baseGas, uint256 gasPrice, address gasToken, address refundReceiver, bytes signatures) payable returns (bool)',
])

/**
 * Concatenate ECDSA signatures from Safe Tx Service confirmations into the
 * single bytes blob the Safe contract expects. Order matters: Safe's
 * `checkSignatures` requires signers in strictly ascending address order
 * (`require(currentOwner > lastOwner)`), so we sort by lowercase owner.
 */
export function concatSafeSignatures(
  confirmations: { owner: string; signature: string }[],
): Hex {
  const sorted = [...confirmations].sort((a, b) =>
    a.owner.toLowerCase() < b.owner.toLowerCase() ? -1 : 1,
  )
  return ('0x' +
    sorted.map((c) => c.signature.replace(/^0x/, '')).join('')) as Hex
}

/** Encode a Safe `execTransaction(...)` call. The returned hex is the calldata
 *  to send with `to = safeAddress` and `value = 0`. */
export function encodeExecTransaction(tx: SafeTx, signatures: Hex): Hex {
  return SAFE_EXEC_IFACE.encodeFunctionData('execTransaction', [
    getAddress(tx.to),
    tx.value,
    tx.data,
    tx.operation,
    tx.safeTxGas,
    tx.baseGas,
    tx.gasPrice,
    getAddress(tx.gasToken),
    getAddress(tx.refundReceiver),
    signatures,
  ]) as Hex
}

export interface InnerCall {
  to: string
  value: bigint
  data: Hex
  /** 0 = CALL, 1 = DELEGATECALL. Always 0 for module-routed dApp calls. */
  operation?: 0 | 1
}

/**
 * Wrap a dApp call (target + value + data) so it runs as a Main-Safe
 * execTransactionFromModule. The returned object is what gets posted as
 * the Module Safe's pending tx (its `to` is the Main Safe).
 */
export function wrapAsModuleCall(
  mainSafe: string,
  inner: InnerCall,
): { to: string; value: string; data: Hex } {
  const data = MAIN_SAFE_IFACE.encodeFunctionData('execTransactionFromModule', [
    getAddress(inner.to),
    inner.value,
    inner.data,
    inner.operation ?? 0,
  ]) as Hex
  return { to: getAddress(mainSafe), value: '0', data }
}

/** EIP-712 typed data for a Safe transaction. The Module Safe is the
 *  verifyingContract — Ainura signs against the Module Safe's domain. */
export interface SafeTx {
  to: string
  value: string
  data: Hex
  operation: 0 | 1
  safeTxGas: string
  baseGas: string
  gasPrice: string
  gasToken: string
  refundReceiver: string
  nonce: number
}

export function emptyGasParams(): Pick<
  SafeTx,
  'safeTxGas' | 'baseGas' | 'gasPrice' | 'gasToken' | 'refundReceiver'
> {
  return {
    safeTxGas: '0',
    baseGas: '0',
    gasPrice: '0',
    gasToken: ZeroAddress,
    refundReceiver: ZeroAddress,
  }
}

/** EIP-712 typed data for the wallet to sign. */
export function safeTxTypedData(
  chainId: number,
  verifyingContract: string,
  tx: SafeTx,
): {
  domain: { chainId: number; verifyingContract: string }
  types: Record<string, { name: string; type: string }[]>
  primaryType: 'SafeTx'
  message: SafeTx
} {
  return {
    domain: { chainId, verifyingContract: getAddress(verifyingContract) },
    primaryType: 'SafeTx',
    types: {
      SafeTx: [
        { name: 'to', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'data', type: 'bytes' },
        { name: 'operation', type: 'uint8' },
        { name: 'safeTxGas', type: 'uint256' },
        { name: 'baseGas', type: 'uint256' },
        { name: 'gasPrice', type: 'uint256' },
        { name: 'gasToken', type: 'address' },
        { name: 'refundReceiver', type: 'address' },
        { name: 'nonce', type: 'uint256' },
      ],
    },
    message: tx,
  }
}

/**
 * Compute the safeTxHash locally so we can post `contractTransactionHash`
 * to Safe Tx Service without round-tripping. Matches the Safe v1.3.0+ scheme
 * — the same hash the contract verifies in execTransaction.
 */
export function computeSafeTxHash(
  chainId: number,
  verifyingContract: string,
  tx: SafeTx,
): Hex {
  const abi = AbiCoder.defaultAbiCoder()
  const SAFE_TX_TYPEHASH = keccak256(
    new TextEncoder().encode(
      'SafeTx(address to,uint256 value,bytes data,uint8 operation,uint256 safeTxGas,uint256 baseGas,uint256 gasPrice,address gasToken,address refundReceiver,uint256 nonce)',
    ),
  )
  const DOMAIN_TYPEHASH = keccak256(
    new TextEncoder().encode(
      'EIP712Domain(uint256 chainId,address verifyingContract)',
    ),
  )
  const domainSeparator = keccak256(
    abi.encode(
      ['bytes32', 'uint256', 'address'],
      [DOMAIN_TYPEHASH, chainId, getAddress(verifyingContract)],
    ),
  )
  const dataHash = keccak256(tx.data)
  const safeTxHashStruct = keccak256(
    abi.encode(
      [
        'bytes32',
        'address',
        'uint256',
        'bytes32',
        'uint8',
        'uint256',
        'uint256',
        'uint256',
        'address',
        'address',
        'uint256',
      ],
      [
        SAFE_TX_TYPEHASH,
        getAddress(tx.to),
        tx.value,
        dataHash,
        tx.operation,
        tx.safeTxGas,
        tx.baseGas,
        tx.gasPrice,
        getAddress(tx.gasToken),
        getAddress(tx.refundReceiver),
        tx.nonce,
      ],
    ),
  )
  return keccak256(
    `0x19${'01'}${domainSeparator.slice(2)}${safeTxHashStruct.slice(2)}` as Hex,
  ) as Hex
}

const MULTI_SEND_ABI = [
  {
    type: 'function',
    name: 'multiSend',
    inputs: [{ type: 'bytes', name: 'transactions' }],
    outputs: [],
    stateMutability: 'payable',
  },
] as const

export interface BatchCall {
  operation?: 0 | 1
  to: string
  value?: bigint
  data: Hex
}

/**
 * Encode a list of calls into a `multiSend(bytes)` calldata payload, ready
 * to be DELEGATECALL'd via MultiSendCallOnly. Uses viem's encodePacked +
 * encodeFunctionData since the user asked for viem here.
 */
export function encodeMultiSend(calls: BatchCall[]): Hex {
  const packed = calls.map((c) => {
    const data = (c.data ?? '0x') as ViemHex
    // dataLength is bytes count, not hex chars
    const len = BigInt((data.length - 2) / 2)
    return viemEncodePacked(
      ['uint8', 'address', 'uint256', 'uint256', 'bytes'],
      [c.operation ?? 0, getAddress(c.to) as ViemHex, c.value ?? 0n, len, data],
    )
  })
  const transactions = viemConcat(packed)
  return viemEncodeFunctionData({
    abi: MULTI_SEND_ABI,
    functionName: 'multiSend',
    args: [transactions],
  }) as Hex
}

/**
 * EIP-712 typed data for the Safe Transaction Service "delete from queue"
 * endpoint. The signer must be a Safe owner. `totp` is hour-bucketed Unix
 * time so signatures expire after ~1h, which prevents indefinite replay.
 * Mirrors safe-wallet-monorepo's signTxServiceMessage exactly so the same
 * payload the official UI sends is what we send.
 */
export function deleteRequestTypedData(
  chainId: number,
  safeAddress: string,
  safeTxHash: Hex,
): {
  domain: {
    name: string
    version: string
    chainId: number
    verifyingContract: string
  }
  types: Record<string, { name: string; type: string }[]>
  primaryType: 'DeleteRequest'
  message: { safeTxHash: Hex; totp: bigint }
} {
  return {
    domain: {
      name: 'Safe Transaction Service',
      version: '1.0',
      chainId,
      verifyingContract: getAddress(safeAddress),
    },
    primaryType: 'DeleteRequest',
    types: {
      DeleteRequest: [
        { name: 'safeTxHash', type: 'bytes32' },
        { name: 'totp', type: 'uint256' },
      ],
    },
    message: {
      safeTxHash,
      totp: BigInt(Math.floor(Date.now() / 3600e3)),
    },
  }
}

/**
 * Wrap a list of calls (EIP-5792 wallet_sendCalls) as a single Module Safe
 * transaction. Uses execTransactionFromModule with operation=1 so the Main
 * Safe DELEGATECALLs into MultiSendCallOnly — each inner call then runs as
 * if msg.sender were the Main Safe.
 */
export function wrapAsMultiSendModuleCall(
  mainSafe: string,
  multiSendCallOnly: string,
  calls: BatchCall[],
): { to: string; value: string; data: Hex; operation: 1 } {
  const innerData = encodeMultiSend(calls)
  const data = MAIN_SAFE_IFACE.encodeFunctionData('execTransactionFromModule', [
    getAddress(multiSendCallOnly),
    0n,
    innerData,
    1, // DELEGATECALL
  ]) as Hex
  return { to: getAddress(mainSafe), value: '0', data, operation: 1 }
}
